import { z } from 'zod';
/* ==========================================================================
 *  Planning schemas  (ADR-0029 / T16.2 / ISSUE-077)
 *
 *  Positioning: plans are optional, swappable suggestions that help reach an
 *  existing adaptive target (ADR-0026). No adherence %, no "off-plan" state,
 *  no streak (D-07). ED-safety: additive-only, banned-lexicon battery applies.
 * ========================================================================== */
// ── Shared vocabulary ─────────────────────────────────────────────────────────
/** meal slots re-use the existing vocabulary from the rest of the app. */
export const mealSlotSchema = z.enum(['breakfast', 'lunch', 'dinner', 'snack', 'other']);
/** 0 = Monday … 6 = Sunday (ISO week). */
export const dayOfWeekSchema = z.number().int().min(0).max(6);
/** Explicit day type — always from the ADR-0026 user schedule, never from steps. */
export const dayTypeSchema = z.enum(['training', 'rest']).nullable();
/** Fill-mode: deterministic (default, €0) or AI-powered (metered, premium). */
export const planFillModeSchema = z.enum(['deterministic', 'ai']);
export const planSourceSchema = z.enum([
    'manual',
    'template',
    'build_rest_deterministic',
    'build_rest_ai',
]);
// ── Template schemas ──────────────────────────────────────────────────────────
export const planTemplateItemSchema = z.object({
    id: z.string().uuid(),
    templateId: z.string().uuid(),
    dayOfWeek: dayOfWeekSchema,
    dayType: dayTypeSchema,
    mealSlot: z.string(),
    savedMealId: z.string().uuid().nullable(),
    recipeId: z.string().uuid().nullable(),
    label: z.string().nullable(),
    sortOrder: z.number().int(),
});
export const planTemplateSchema = z.object({
    id: z.string().uuid(),
    userId: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    isArchived: z.boolean(),
    items: z.array(planTemplateItemSchema),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/** List item (no items array — saves bandwidth on list view). */
export const planTemplateListItemSchema = planTemplateSchema.omit({ items: true });
export const createPlanTemplateSchema = z.object({
    name: z.string().min(1).max(120),
    description: z.string().max(500).optional(),
    items: z
        .array(z.object({
        dayOfWeek: dayOfWeekSchema,
        dayType: dayTypeSchema.optional(),
        mealSlot: z.string().min(1),
        savedMealId: z.string().uuid().optional(),
        recipeId: z.string().uuid().optional(),
        label: z.string().max(200).optional(),
        sortOrder: z.number().int().optional(),
    }))
        .default([]),
});
export const updatePlanTemplateSchema = z.object({
    name: z.string().min(1).max(120).optional(),
    description: z.string().max(500).nullable().optional(),
    isArchived: z.boolean().optional(),
    items: z
        .array(z.object({
        id: z.string().uuid().optional(),
        dayOfWeek: dayOfWeekSchema,
        dayType: dayTypeSchema.optional(),
        mealSlot: z.string().min(1),
        savedMealId: z.string().uuid().nullable().optional(),
        recipeId: z.string().uuid().nullable().optional(),
        label: z.string().max(200).nullable().optional(),
        sortOrder: z.number().int().optional(),
    }))
        .optional(),
});
export const applyTemplateBodySchema = z.object({
    /** ISO date of any day within the target week (resolved to week start = Monday). */
    weekStartDate: z.string().date(),
    /** When true, overwrite existing plan items for the week. Defaults to false (merge). */
    overwrite: z.boolean().default(false),
});
// ── Meal plan (dated) schemas ─────────────────────────────────────────────────
export const mealPlanItemSchema = z.object({
    id: z.string().uuid(),
    planId: z.string().uuid(),
    mealSlot: z.string(),
    savedMealId: z.string().uuid().nullable(),
    recipeId: z.string().uuid().nullable(),
    label: z.string().nullable(),
    isLogged: z.boolean(),
    loggedEntryId: z.string().uuid().nullable(),
    sortOrder: z.number().int(),
});
export const mealPlanSchema = z.object({
    id: z.string().uuid(),
    userId: z.string(),
    planDate: z.string().date(),
    dayType: dayTypeSchema,
    source: planSourceSchema,
    templateId: z.string().uuid().nullable(),
    items: z.array(mealPlanItemSchema),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const updateMealPlanItemSchema = z.object({
    isLogged: z.boolean().optional(),
    loggedEntryId: z.string().uuid().nullable().optional(),
    mealSlot: z.string().optional(),
    savedMealId: z.string().uuid().nullable().optional(),
    recipeId: z.string().uuid().nullable().optional(),
    label: z.string().max(200).nullable().optional(),
    sortOrder: z.number().int().optional(),
});
// ── Build-rest-of-today schemas ───────────────────────────────────────────────
export const planFillBodySchema = z.object({
    date: z.string().date(),
});
/** One suggestion item returned by the deterministic fill. */
export const planFillSuggestionSchema = z.object({
    mealSlot: z.string(),
    savedMealId: z.string().uuid().nullable(),
    label: z.string(),
    kcal: z.number(),
    proteinG: z.number(),
    carbsG: z.number(),
    fatG: z.number(),
    fiberG: z.number(),
    sortOrder: z.number().int(),
});
export const planFillResponseSchema = z.object({
    date: z.string().date(),
    mode: planFillModeSchema,
    plan: mealPlanSchema,
    suggestions: z.array(planFillSuggestionSchema),
    /** Battery credit spent (0 for deterministic, 1 for AI). */
    creditSpent: z.number().int(),
    /** Remaining macros gap AFTER the suggestions are applied (estimate). */
    remainingAfter: z
        .object({
        kcal: z.number(),
        proteinG: z.number(),
        carbsG: z.number(),
        fatG: z.number(),
        fiberG: z.number(),
    })
        .nullable(),
});
// ── Grocery list schemas ──────────────────────────────────────────────────────
export const groceryItemSchema = z.object({
    /** Display name of the food/ingredient. */
    name: z.string(),
    /** Aggregated quantity (null if incompatible units kept separate). */
    quantity: z.number().nullable(),
    unit: z.string().nullable(),
    /** User has checked it off (has it / already bought). */
    checked: z.boolean(),
});
export const groceryListQuerySchema = z.object({
    /** Inclusive start of the date range (YYYY-MM-DD). */
    from: z.string().date(),
    /** Inclusive end of the date range (YYYY-MM-DD). */
    to: z.string().date(),
});
export const groceryListResponseSchema = z.object({
    from: z.string().date(),
    to: z.string().date(),
    items: z.array(groceryItemSchema),
    /** ISO datetime the list was generated (for display). */
    generatedAt: z.string().datetime(),
});
//# sourceMappingURL=planning.js.map